import { FeetInchesInput } from "@/shared/components/FeetInchesInput";
import { GradeSelector } from "@/shared/selectors/GradeSelector";
import { HeatSelector } from "@/shared/selectors/HeatSelector";
import { SectionSelector } from "@/shared/selectors/SectionSelector";
import { useSections } from "@/shared/selectors/SectionsOfPartSelector";
import { backgroundColor } from "@/utils/theme";
import { CheckCircleOutline, Close, Delete, Edit } from "@mui/icons-material";
import {
	Box,
	IconButton,
	InputAdornment,
	Stack,
	TableCell,
	TableRow,
	TextField,
	Tooltip,
} from "@mui/material";
import { useEffect, useState } from "react";
import { StackerBundle, StackerBundleDraft } from "../models/StackerBundle";
import { centerSx, dimSx, formatLength, numCellSx } from "../utils/utils";
import { MILL_2 } from "@/setup/mill-config/mill-config";

const formatBundlesPcs = (bundles: number | null, pcs: number | null) => {
	if (!bundles && !pcs) return "-";
	return `${bundles ?? 0} × ${pcs ?? 0}`;
};

export const DisplayRow = ({
	idx,
	row,
	disabled,
	onEdit,
	onDelete,
}: {
	idx: number;
	row: StackerBundle;
	disabled: boolean;
	onEdit: () => void;
	onDelete: () => void;
}) => {
	const autoTotal = (row.GoodBundles || 0) * (row.GoodPiecesPerBundle || 0);
	const isManualTotal = row.TotalPieces !== autoTotal;

	return (
		<TableRow
			hover
			sx={{
				"&:hover .row-actions": { opacity: 1 },
				"& td": { verticalAlign: "middle" },
				"&:last-child td, &:last-child th": { border: 0 },
			}}
		>
			<TableCell sx={dimSx}>{idx}</TableCell>
			<TableCell>{row.SectionName}</TableCell>
			<TableCell sx={row.HeatName ? undefined : dimSx}>
				{row.HeatName || "-"}
			</TableCell>
			<TableCell>{row.GradeName}</TableCell>
			<TableCell sx={{ fontVariantNumeric: "tabular-nums" }}>
				{formatLength(row.LengthInches)}
			</TableCell>
			<TableCell sx={{ ...centerSx, fontVariantNumeric: "tabular-nums" }}>
				{row.GoodBundles} × {row.GoodPiecesPerBundle}
			</TableCell>
			<TableCell sx={numCellSx}>
				{isManualTotal && (
					<Tooltip
						title={`Manual override — auto-calculated value is ${autoTotal?.toLocaleString()}`}
					>
						<Box
							component="span"
							sx={{
								display: "inline-flex",
								alignItems: "center",
								justifyContent: "center",
								width: 18,
								height: 18,
								mr: 0.75,
								borderRadius: "50%",
								bgcolor: "#fff3cd",
								color: "#6a4b00",
								fontSize: 11,
								fontWeight: 700,
								lineHeight: 1,
								verticalAlign: "middle",
							}}
						>
							M
						</Box>
					</Tooltip>
				)}
				{row.TotalPieces?.toLocaleString()}
			</TableCell>
			<TableCell sx={{ ...numCellSx, ...(row.TotalWeight ? {} : dimSx) }}>
				{row.TotalWeight != null ? row.TotalWeight?.toLocaleString() : "-"}
			</TableCell>
			<TableCell
				sx={{
					...centerSx,
					...(row.ScrapBundles || row.ScrapPiecesPerBundle ? {} : dimSx),
					fontVariantNumeric: "tabular-nums",
				}}
			>
				{formatBundlesPcs(row.ScrapBundles, row.ScrapPiecesPerBundle)}
			</TableCell>
			<TableCell sx={row.ScrapReason ? undefined : dimSx}>
				{row.ScrapReason || "-"}
			</TableCell>
			<TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
				<Box
					className="row-actions"
					sx={{ opacity: 0, transition: "opacity .1s" }}
				>
					<IconButton
						size="small"
						title="Edit row"
						disabled={disabled}
						onClick={onEdit}
					>
						<Edit fontSize="small" />
					</IconButton>
					<IconButton
						size="small"
						title="Delete row"
						color="error"
						disabled={disabled}
						onClick={onDelete}
					>
						<Delete fontSize="small" />
					</IconButton>
				</Box>
			</TableCell>
		</TableRow>
	);
};

export const EditRow = ({
	idx,
	draft,
	original,
	isMutating,
	onChange,
	onSave,
	onCancel,
}: {
	idx: number;
	draft: StackerBundleDraft;
	original?: StackerBundle;
	isMutating: boolean;
	onChange: (next: StackerBundleDraft) => void;
	onSave: () => void;
	onCancel: () => void;
}) => {
	const autoTotal = (draft.GoodBundles || 0) * (draft.GoodPiecesPerBundle || 0);

	useEffect(() => {
		if (!draft.TotalPiecesIsManual && draft.TotalPieces !== autoTotal) {
			onChange({ ...draft, TotalPieces: autoTotal });
		}
	}, [draft.GoodBundles, draft.GoodPiecesPerBundle, draft.TotalPiecesIsManual]);

	const { data: sectionsData } = useSections({});
	const lbsPerFoot =
		sectionsData?.sections.find((s) => s.SectionId === draft.SectionId)
			?.LBsPerFoot ?? null;
	const totalPiecesForWeight = draft.TotalPieces ?? autoTotal;
	const calculatedTotalWeight =
		lbsPerFoot != null && draft.LengthInches > 0
			? Math.round(
					((lbsPerFoot * (draft.LengthInches / 12) * totalPiecesForWeight) /
						2000) *
						100,
				) / 100
			: null;

	// By default keep the stored value from the table; only recalculate live
	// when an input that feeds the formula changes (or for a new row).
	const weightInputsChanged =
		!original ||
		original.SectionId !== draft.SectionId ||
		original.LengthInches !== draft.LengthInches ||
		original.GoodBundles !== draft.GoodBundles ||
		original.GoodPiecesPerBundle !== draft.GoodPiecesPerBundle;
	const totalWeight = weightInputsChanged
		? calculatedTotalWeight
		: (original?.TotalWeight ?? null);

	// Keep the draft's TotalWeight in sync so it is persisted on save
	useEffect(() => {
		if (draft.TotalWeight !== totalWeight) {
			onChange({ ...draft, TotalWeight: totalWeight });
		}
	}, [totalWeight]);

	const isNew = !original;
	const isDirty =
		isNew ||
		original.SectionId !== draft.SectionId ||
		original.HeatId !== draft.HeatId ||
		original.LengthInches !== draft.LengthInches ||
		original.GoodBundles !== draft.GoodBundles ||
		original.GoodPiecesPerBundle !== draft.GoodPiecesPerBundle ||
		original.TotalPieces !== draft.TotalPieces ||
		original.ScrapBundles !== draft.ScrapBundles ||
		original.ScrapPiecesPerBundle !== draft.ScrapPiecesPerBundle ||
		original.ScrapReason !== draft.ScrapReason;

	const canSave =
		isDirty &&
		draft.SectionId > 0 &&
		draft.LengthInches > 0 &&
		draft.GoodBundles >= 0 &&
		draft.GoodPiecesPerBundle >= 0 &&
		!isMutating;

	return (
		<TableRow
			sx={{
				backgroundColor: backgroundColor,
				"& td": {
					verticalAlign: "middle",
					py: 0.75,
				},
			}}
		>
			<TableCell sx={dimSx}>{idx}</TableCell>

			<TableCell>
				<SectionSelector
					value={draft.SectionId}
					onChange={(value) => onChange({ ...draft, SectionId: value! })}
					size="small"
					sortBy="recent"
					hideTph
					assetId={MILL_2.id}
				/>
			</TableCell>

			<TableCell>
				<HeatSelector
					value={draft.HeatId}
					onChange={(value, heat) =>
						onChange({
							...draft,
							HeatId: value ?? null,
							GradeId: heat?.GradeId!,
						})
					}
					sectionId={draft.SectionId}
					assetId={MILL_2.id}
				/>
			</TableCell>

			<TableCell>
				<GradeSelector value={draft.GradeId} disabled assetId={MILL_2.id} />
			</TableCell>

			<TableCell>
				<FeetInchesInput
					value={draft.LengthInches}
					onChange={(v) => onChange({ ...draft, LengthInches: v ?? 0 })}
				/>
			</TableCell>

			<TableCell sx={centerSx}>
				<BundlesPcsInput
					bundles={draft.GoodBundles}
					pieces={draft.GoodPiecesPerBundle}
					onChange={(b, p) =>
						onChange({
							...draft,
							GoodBundles: b ?? 0,
							GoodPiecesPerBundle: p ?? 0,
							TotalPieces: (b ?? 0) * (p ?? 0),
						})
					}
				/>
			</TableCell>

			<TableCell sx={numCellSx}>
				<TextField
					size="small"
					type="number"
					margin="none"
					fullWidth
					value={draft.TotalPieces ?? ""}
					onChange={(e) => {
						const v = e.target.value === "" ? null : Number(e.target.value);
						onChange({
							...draft,
							TotalPieces: v,
							TotalPiecesIsManual: v !== null && v !== autoTotal,
						});
					}}
					onBlur={() => {
						const isManual =
							draft.TotalPieces !== null && draft.TotalPieces !== autoTotal;
						if (isManual !== draft.TotalPiecesIsManual) {
							onChange({ ...draft, TotalPiecesIsManual: isManual });
						}
					}}
					disabled
					inputProps={{ style: { textAlign: "right" } }}
					InputProps={{
						startAdornment:
							draft.TotalPieces !== null &&
							draft.TotalPieces !== autoTotal &&
							false ? (
								<InputAdornment position="start" sx={{ ml: 0, mr: 0.5 }}>
									<Tooltip
										title={`Manual override — auto-calculated value is ${autoTotal?.toLocaleString()}`}
									>
										<Box
											sx={{
												width: 18,
												height: 18,
												display: "inline-flex",
												alignItems: "center",
												justifyContent: "center",
												borderRadius: "50%",
												bgcolor: "#fff3cd",
												color: "#6a4b00",
												fontSize: 11,
												fontWeight: 700,
												lineHeight: 1,
											}}
										>
											M
										</Box>
									</Tooltip>
								</InputAdornment>
							) : undefined,
					}}
					sx={{ backgroundColor: "white" }}
				/>
			</TableCell>

			<TableCell sx={numCellSx}>
				<TextField
					size="small"
					type="number"
					margin="none"
					fullWidth
					value={totalWeight ?? 0}
					disabled
					inputProps={{ style: { textAlign: "right" } }}
					sx={{ backgroundColor: "white" }}
				/>
			</TableCell>

			<TableCell sx={centerSx}>
				<BundlesPcsInput
					bundles={draft.ScrapBundles}
					pieces={draft.ScrapPiecesPerBundle}
					onChange={(b, p) =>
						onChange({
							...draft,
							ScrapBundles: b,
							ScrapPiecesPerBundle: p,
						})
					}
				/>
			</TableCell>

			<TableCell>
				<TextField
					size="small"
					margin="none"
					fullWidth
					value={draft.ScrapReason ?? ""}
					onChange={(e) =>
						onChange({
							...draft,
							ScrapReason: e.target.value === "" ? null : e.target.value,
						})
					}
					onFocus={(e) => e.target.select()}
					sx={{
						backgroundColor: "white",
					}}
				/>
			</TableCell>

			<TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
				<Stack direction="row" spacing={0.5} justifyContent="flex-end">
					<Tooltip title={isDirty ? "Save changes" : "No changes to save"}>
						<Box component="span">
							<IconButton
								size="small"
								color="success"
								onClick={onSave}
								disabled={!canSave}
							>
								<CheckCircleOutline fontSize="small" />
							</IconButton>
						</Box>
					</Tooltip>
					<Tooltip title="Cancel">
						<IconButton size="small" onClick={onCancel} disabled={isMutating}>
							<Close fontSize="small" />
						</IconButton>
					</Tooltip>
				</Stack>
			</TableCell>
		</TableRow>
	);
};

const BundlesPcsInput = ({
	bundles,
	pieces,
	placeholder,
	onChange,
}: {
	bundles: number | null;
	pieces: number | null;
	placeholder?: string;
	onChange: (bundles: number | null, pieces: number | null) => void;
}) => {
	const [bundlesState, setBundlesState] = useState(
		bundles === null ? "" : String(bundles),
	);
	const [piecesState, setPiecesState] = useState(
		pieces === null ? "" : String(pieces),
	);

	useEffect(() => {
		setBundlesState(bundles === null ? "" : String(bundles));
		setPiecesState(pieces === null ? "" : String(pieces));
	}, [bundles, pieces]);

	return (
		<Stack
			direction="row"
			alignItems="center"
			justifyContent="center"
			gap={0.5}
			sx={{ width: "100%" }}
		>
			<TextField
				size="small"
				margin="none"
				value={bundlesState}
				placeholder={placeholder}
				onChange={(e) => {
					const raw = e.target.value.replace(/[^0-9]/g, "");
					setBundlesState(raw);
					const parseN = (s: string) => (s === "" ? null : Number(s));
					onChange(parseN(raw), parseN(piecesState));
				}}
				onFocus={(e) => e.target.select()}
				inputProps={{
					inputMode: "numeric" as const,
					style: { textAlign: "right" as const },
				}}
				sx={{ flex: 1, minWidth: 0, backgroundColor: "white" }}
			/>
			<Box
				component="span"
				sx={{ color: "text.secondary", px: 0.25, flexShrink: 0 }}
			>
				×
			</Box>
			<TextField
				size="small"
				margin="none"
				value={piecesState}
				placeholder={placeholder}
				onChange={(e) => {
					const raw = e.target.value.replace(/[^0-9]/g, "");
					setPiecesState(raw);

					const parseN = (s: string) => (s === "" ? null : Number(s));
					onChange(parseN(bundlesState), parseN(raw));
				}}
				onFocus={(e) => e.target.select()}
				inputProps={{
					inputMode: "numeric" as const,
					style: { textAlign: "right" as const },
				}}
				sx={{ flex: 1, minWidth: 0, backgroundColor: "white" }}
			/>
		</Stack>
	);
};
